// Brands API - GraphQL solution for fetching all brands
class BrandsAPI {
  constructor() {
    this.shopDomain = window.Shopify.shop;
    this.apiVersion = '2024-01';
    this.logoCache = new Map();
  }

  extractUrlFromValue(value) {
    if (typeof value !== 'string') {
      return null;
    }

    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'string' && parsed.startsWith('http')) {
        return parsed;
      }

      if (parsed?.url && parsed.url.startsWith('http')) {
        return parsed.url;
      }

      if (parsed?.file?.url && parsed.file.url.startsWith('http')) {
        return parsed.file.url;
      }

      if (parsed?.src && parsed.src.startsWith('http')) {
        return parsed.src;
      }
    } catch (error) {
      // Ignore JSON parse errors – value might just be a plain string
    }

    if (value.startsWith('http')) {
      return value;
    }

    return null;
  }

  normalizeBrandEntry(brand) {
    const normalized = { ...brand };
    let logoCandidate = normalized.logo;

    if (typeof logoCandidate !== 'string') {
      logoCandidate = this.extractUrlFromValue(logoCandidate);
    }

    if (typeof logoCandidate === 'string') {
      if (logoCandidate.startsWith('//')) {
        logoCandidate = `https:${logoCandidate}`;
      } else if (!logoCandidate.startsWith('http')) {
        const extracted = this.extractUrlFromValue(logoCandidate);
        logoCandidate = extracted && extracted.startsWith('http') ? extracted : null;
      }
    } else {
      logoCandidate = null;
    }

    normalized.logo = logoCandidate || null;

    if (typeof normalized.logo_alt !== 'string' || normalized.logo_alt.trim() === '') {
      normalized.logo_alt = normalized.navn || '';
    }

    return normalized;
  }

  async resolveLogoReferences(brands) {
    const token = window?.Shopify?.storefrontAccessToken;

    if (!token) {
      return brands;
    }

    const unresolvedIds = new Set();

    brands.forEach(brand => {
      if (typeof brand.logo === 'string' && brand.logo.startsWith('gid://')) {
        if (this.logoCache.has(brand.logo)) {
          const cached = this.logoCache.get(brand.logo);
          brand.logo = cached.url || null;
          if (!brand.logo_alt && cached.alt) {
            brand.logo_alt = cached.alt;
          }
        } else {
          unresolvedIds.add(brand.logo);
        }
      }
    });

    if (unresolvedIds.size === 0) {
      return brands;
    }

    const idsArray = Array.from(unresolvedIds);
    const chunkSize = 20;

    const query = `
      query ResolveLogos($ids: [ID!]!) {
        nodes(ids: $ids) {
          id
          __typename
          ... on MediaImage {
            image {
              url(transform: {maxWidth: 800, maxHeight: 800, crop: CENTER})
              altText
            }
          }
          ... on GenericFile {
            url
            previewImage {
              url
              altText
            }
          }
        }
      }
    `;

    for (let i = 0; i < idsArray.length; i += chunkSize) {
      const batch = idsArray.slice(i, i + chunkSize);

      try {
        const response = await fetch(`https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({
            query,
            variables: {
              ids: batch
            }
          })
        });

        const data = await response.json();

        if (!response.ok || data.errors) {
          console.error('GraphQL errors while resolving logos:', data.errors || response.statusText);
          continue;
        }

        const nodes = data?.data?.nodes || [];
        nodes.forEach(node => {
          if (!node?.id) {
            return;
          }

          let url = null;
          let alt = null;

          if (node.__typename === 'MediaImage') {
            url = node.image?.url || null;
            alt = node.image?.altText || null;
          } else if (node.__typename === 'GenericFile') {
            url = node.previewImage?.url || node.url || null;
            alt = node.previewImage?.altText || null;
          }

          this.logoCache.set(node.id, { url, alt });
        });
      } catch (error) {
        console.error('Error resolving logo references:', error);
      }
    }

    brands.forEach(brand => {
      if (typeof brand.logo === 'string' && brand.logo.startsWith('gid://')) {
        const cached = this.logoCache.get(brand.logo);
        if (cached?.url) {
          brand.logo = cached.url;
          if (!brand.logo_alt && cached.alt) {
            brand.logo_alt = cached.alt;
          }
        } else {
          brand.logo = null;
        }
      }
    });

    return brands;
  }

  async fetchAllBrands() {
    try {
      const query = `
        query GetAllBrands($first: Int!, $after: String) {
          metaobjects(type: "brand", first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `;

      let allBrands = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const response = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Access-Token': 'YOUR_ACCESS_TOKEN_HERE' // Replace with your app's access token
          },
          body: JSON.stringify({
            query: query,
            variables: {
              first: 250, // Maximum allowed
              after: cursor
            }
          })
        });

        const data = await response.json();
        
        if (data.errors) {
          console.error('GraphQL errors:', data.errors);
          break;
        }

        const brands = data.data.metaobjects.edges.map(edge => {
          const brand = {
            id: edge.node.id,
            handle: edge.node.handle
          };
          
          // Extract fields and map to expected names
          edge.node.fields.forEach(field => {
            if (field.key === 'navn') {
              brand.navn = field.value;
            } else if (field.key === 'logo') {
              brand.logo = field.value;
            } else if (field.key === 'kollektion_url') {
              brand.kollektion_url = field.value;
            } else if (field.key === 'description') {
              brand.description = field.value;
            }
          });
          
          return brand;
        });

        allBrands = allBrands.concat(brands);
        
        hasNextPage = data.data.metaobjects.pageInfo.hasNextPage;
        cursor = data.data.metaobjects.pageInfo.endCursor;
      }

      return allBrands;
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  }

  // Fetch a single page of brands through the Storefront API
  async fetchBrandsPage({ after = null, first = 50 } = {}) {
    const token = window?.Shopify?.storefrontAccessToken;

    if (!token) {
      console.warn('Storefront access token mangler – vender tilbage til Liquid fallback.');
      return {
        brands: [],
        hasNextPage: false,
        endCursor: null
      };
    }

    try {
      const pageSize = Math.min(first, 50); // Storefront API tillader maks 50 ad gangen

      const query = `
        query GetAllBrands($first: Int!, $after: String) {
          metaobjects(type: "brand", first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                handle
                navn: field(key: "navn") {
                  value
                }
                description: field(key: "description") {
                  value
                }
                kollektion_url: field(key: "kollektion_url") {
                  value
                }
                logo: field(key: "logo") {
                  value
                  reference {
                    __typename
                    ... on MediaImage {
                      image {
                        url(transform: {maxWidth: 800, maxHeight: 800, crop: CENTER})
                        altText
                      }
                    }
                    ... on GenericFile {
                      url
                      previewImage {
                        url
                        altText
                      }
                    }
                    ... on Metaobject {
                      handle
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({
          query: query,
          variables: {
            first: pageSize,
            after
          }
        })
      });

      const data = await response.json();

      if (!response.ok || data.errors) {
        console.error('GraphQL errors:', data.errors || response.statusText);
        return {
          brands: [],
          hasNextPage: false,
          endCursor: null
        };
      }

      const metaobjects = data?.data?.metaobjects;
      if (!metaobjects) {
        console.warn('Unexpected response when fetching brands:', data);
        return {
          brands: [],
          hasNextPage: false,
          endCursor: null
        };
      }

      console.debug('Brands page info', {
        received: metaobjects.edges.length,
        hasNextPage: metaobjects.pageInfo?.hasNextPage,
        endCursorSample: metaobjects.pageInfo?.endCursor
      });

      const rawBrands = metaobjects.edges.map(edge => {
        const node = edge.node;
        const logoField = node.logo;

        const mediaImageUrl = logoField?.reference?.image?.url;
        const mediaImageAlt = logoField?.reference?.image?.altText;
        const genericFileUrl = logoField?.reference?.url;
        const genericFilePreviewUrl = logoField?.reference?.previewImage?.url;
        const genericFilePreviewAlt = logoField?.reference?.previewImage?.altText;

        let logo = null;
        let logoAlt = null;

        if (mediaImageUrl) {
          logo = mediaImageUrl;
          logoAlt = mediaImageAlt;
        } else if (genericFilePreviewUrl) {
          logo = genericFilePreviewUrl;
          logoAlt = genericFilePreviewAlt;
        } else if (genericFileUrl) {
          logo = genericFileUrl;
        } else if (logoField?.value) {
          logo = logoField.value;
        }

        const brand = {
          id: node.id,
          handle: node.handle,
          navn: node.navn?.value || '',
          kollektion_url: node.kollektion_url?.value || '',
          description: node.description?.value || '',
          logo,
          logo_alt: logoAlt
        };

        return this.normalizeBrandEntry(brand);
      });

      await this.resolveLogoReferences(rawBrands);

      return {
        brands: rawBrands,
        hasNextPage: metaobjects.pageInfo.hasNextPage,
        endCursor: metaobjects.pageInfo.endCursor
      };
    } catch (error) {
      console.error('Error fetching brands:', error);
      return {
        brands: [],
        hasNextPage: false,
        endCursor: null
      };
    }
  }

  // Alternative method using Storefront API (no authentication required)
  async fetchAllBrandsStorefront() {
    try {
      let allBrands = [];
      let cursor = null;
      let hasNextPage = true;
      let safetyCounter = 0;

      while (hasNextPage && safetyCounter < 20) {
        const { brands, hasNextPage: nextPageExists, endCursor } = await this.fetchBrandsPage({
          after: cursor,
          first: 50
        });

        allBrands = allBrands.concat(brands);
        hasNextPage = nextPageExists && Boolean(endCursor);
        cursor = endCursor;
        safetyCounter += 1;
      }

      console.log(`Successfully loaded ${allBrands.length} brands across all pages`);
      return allBrands;
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  }
}

// Initialize and export
window.BrandsAPI = BrandsAPI;
